import {
  SKIN_FACES,
  SKIN_LAYERS,
  SKIN_PARTS,
  SKIN_SIZE,
  type PixelPoint,
  type PixelRect,
  type SkinFace,
  type SkinLayer,
  type SkinModel,
  type SkinPart,
  type SkinSize,
  type SurfacePoint,
  type SurfaceRef,
  type SurfaceSize,
} from "./types";

interface BoxLayout {
  u: number;
  v: number;
  width: number;
  height: number;
  depth: number;
}

type LayoutTable = Record<
  SkinPart,
  Record<SkinLayer, Omit<BoxLayout, "width"> & { width: number | "arm" }>
>;

/**
 * Canonical Minecraft Java 64x64 skin atlas origins. A box is laid out as:
 *
 *              top       bottom
 *   right      front     left       back
 *
 * Each face's local coordinate is the pixel seen from outside the body, with
 * (0, 0) at that view's top-left. The atlas rectangles themselves follow that
 * visual orientation. Three's bottom-plane vertex rows are reversed, so the
 * geometry adapter compensates there rather than changing this atlas mapping.
 */
const BOX_LAYOUTS: LayoutTable = {
  head: {
    inner: { u: 0, v: 0, width: 8, height: 8, depth: 8 },
    outer: { u: 32, v: 0, width: 8, height: 8, depth: 8 },
  },
  body: {
    inner: { u: 16, v: 16, width: 8, height: 12, depth: 4 },
    outer: { u: 16, v: 32, width: 8, height: 12, depth: 4 },
  },
  rightArm: {
    inner: { u: 40, v: 16, width: "arm", height: 12, depth: 4 },
    outer: { u: 40, v: 32, width: "arm", height: 12, depth: 4 },
  },
  leftArm: {
    inner: { u: 32, v: 48, width: "arm", height: 12, depth: 4 },
    outer: { u: 48, v: 48, width: "arm", height: 12, depth: 4 },
  },
  rightLeg: {
    inner: { u: 0, v: 16, width: 4, height: 12, depth: 4 },
    outer: { u: 0, v: 32, width: 4, height: 12, depth: 4 },
  },
  leftLeg: {
    inner: { u: 16, v: 48, width: 4, height: 12, depth: 4 },
    outer: { u: 0, v: 48, width: 4, height: 12, depth: 4 },
  },
};

function getBoxLayout(
  part: SkinPart,
  layer: SkinLayer,
  model: SkinModel,
  size: SkinSize,
): BoxLayout {
  const layout = BOX_LAYOUTS[part][layer];
  const scale = size / SKIN_SIZE;
  return {
    u: layout.u * scale,
    v: layout.v * scale,
    width:
      (layout.width === "arm" ? (model === "slim" ? 3 : 4) : layout.width) *
      scale,
    height: layout.height * scale,
    depth: layout.depth * scale,
  };
}

export function getSurfaceRect(
  surface: SurfaceRef,
  model: SkinModel,
  size: SkinSize = SKIN_SIZE,
): PixelRect {
  const { u, v, width, height, depth } = getBoxLayout(
    surface.part,
    surface.layer,
    model,
    size,
  );
  switch (surface.face) {
    case "top":
      return { x: u + depth, y: v, width, height: depth };
    case "bottom":
      return { x: u + depth + width, y: v, width, height: depth };
    case "right":
      return { x: u, y: v + depth, width: depth, height };
    case "front":
      return { x: u + depth, y: v + depth, width, height };
    case "left":
      return { x: u + depth + width, y: v + depth, width: depth, height };
    case "back":
      return { x: u + depth + width + depth, y: v + depth, width, height };
  }
}

export function getSurfaceSize(
  surface: SurfaceRef,
  model: SkinModel,
  size: SkinSize = SKIN_SIZE,
): SurfaceSize {
  const { width, height } = getSurfaceRect(surface, model, size);
  return { width, height };
}

export function surfaceToUv(
  surface: SurfaceRef,
  surfaceX: number,
  surfaceY: number,
  model: SkinModel,
  size: SkinSize = SKIN_SIZE,
): PixelPoint {
  const rect = getSurfaceRect(surface, model, size);
  assertIntegerInRange(surfaceX, 0, rect.width - 1, "面内 x");
  assertIntegerInRange(surfaceY, 0, rect.height - 1, "面内 y");
  return { x: rect.x + surfaceX, y: rect.y + surfaceY };
}

/** Returns every logical surface owning a UV pixel. Normally this is zero or one. */
export function uvToSurface(
  uvX: number,
  uvY: number,
  model: SkinModel,
  layer?: SkinLayer,
  size: SkinSize = SKIN_SIZE,
): SurfacePoint[] {
  assertIntegerInRange(uvX, 0, size - 1, "UV x");
  assertIntegerInRange(uvY, 0, size - 1, "UV y");
  const matches: SurfacePoint[] = [];
  const layers = layer ? [layer] : SKIN_LAYERS;

  for (const part of SKIN_PARTS) {
    for (const candidateLayer of layers) {
      for (const face of SKIN_FACES) {
        const surface = {
          part,
          layer: candidateLayer,
          face,
        } satisfies SurfaceRef;
        const rect = getSurfaceRect(surface, model, size);
        if (
          uvX >= rect.x &&
          uvX < rect.x + rect.width &&
          uvY >= rect.y &&
          uvY < rect.y + rect.height
        ) {
          matches.push({ surface, x: uvX - rect.x, y: uvY - rect.y });
        }
      }
    }
  }
  return matches;
}

export function listSurfaces(
  model: SkinModel,
  size: SkinSize = SKIN_SIZE,
): Array<SurfaceRef & PixelRect> {
  return SKIN_PARTS.flatMap((part) =>
    SKIN_LAYERS.flatMap((layer) =>
      SKIN_FACES.map((face) => ({
        part,
        layer,
        face,
        ...getSurfaceRect({ part, layer, face }, model, size),
      })),
    ),
  );
}

function assertIntegerInRange(
  value: number,
  minimum: number,
  maximum: number,
  label: string,
): void {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(
      `${label} 必须是 ${minimum} 到 ${maximum} 的整数，收到：${value}`,
    );
  }
}

export function isSkinFace(value: string): value is SkinFace {
  return (SKIN_FACES as readonly string[]).includes(value);
}
